import { useState } from "react";
import { UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/NativeSelect";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUBJECTS, GRADES, KINDS, ACCEPT, ALLOWED_EXT, MAX_SIZE } from "@/lib/constants";
import { hasProfanity, PROFANITY_WARNING } from "@/lib/profanity";

function checkFile(f) {
  const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
  if (!ALLOWED_EXT.includes(ext)) return "Недопустимый формат. Разрешены: фото, doc, docx, ppt, pptx, pdf, txt";
  if (f.size > MAX_SIZE) return "Файл больше 20 МБ";
  return null;
}

export function UploadDialog({ defaultSubject, onUploaded, trigger }) {
  const { user } = useAuth();
  const empty = { title: "", description: "", subject: defaultSubject || SUBJECTS[0].id, grade: user.grade, kind: "SOR" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = checkFile(f);
    if (err) { toast.error(err); e.target.value = ""; setFile(null); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Выберите файл");
    if (hasProfanity(form.title, form.description)) return toast.warning(PROFANITY_WARNING);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/materials", fd);
      toast.success("Файл отправлен на проверку. После одобрения вы получите 50 коинов");
      setOpen(false); setForm(empty); setFile(null);
      onUploaded?.();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(empty); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="open-upload-dialog-button" className="rounded-full bg-[#1f5f42] hover:bg-[#174731] h-11 px-6">
            <UploadCloud className="h-4 w-4 mr-2" />Загрузить файл
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Загрузить материал</DialogTitle>
          <DialogDescription>Файл появится у всех после проверки модератором.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="up-title">Название</Label>
            <Input id="up-title" required value={form.title} onChange={set("title")} placeholder="Например: СОР №2, вариант А" data-testid="upload-title-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-3 sm:col-span-1">
              <Label>Класс</Label>
              <NativeSelect value={form.grade} onChange={set("grade")} data-testid="upload-grade-select">
                {GRADES.map((g) => <option key={g} value={g}>{g} класс</option>)}
              </NativeSelect>
            </div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label>Предмет</Label>
              <NativeSelect value={form.subject} onChange={set("subject")} data-testid="upload-subject-select">
                {SUBJECTS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label>Тип</Label>
              <NativeSelect value={form.kind} onChange={set("kind")} data-testid="upload-kind-select">
                {KINDS.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </NativeSelect>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="up-desc">Описание (необязательно)</Label>
            <Textarea id="up-desc" rows={2} value={form.description} onChange={set("description")} data-testid="upload-description-input" />
          </div>
          <label className="block border-2 border-dashed border-[#c8e6d3] rounded-xl p-5 text-center cursor-pointer hover:bg-[#e8f5ed] transition-colors">
            <input type="file" accept={ACCEPT} onChange={pick} className="hidden" data-testid="upload-file-input" />
            <UploadCloud className="h-6 w-6 mx-auto text-[#2e7d56]" />
            <div className="mt-2 text-sm font-semibold text-[#1f5f42]" data-testid="upload-file-name">{file ? file.name : "Выберите файл"}</div>
            <div className="text-xs text-[#4b6b5c] mt-1">Фото, doc, docx, ppt, pptx, pdf, txt · до 20 МБ</div>
          </label>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#1f5f42] hover:bg-[#174731] h-11" data-testid="upload-submit-button">
            {busy ? "Загрузка..." : "Отправить на проверку"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
